import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';

/**
 * LOS CLIENTES QUE COMPRAN EN UN COMERCIO — pantalla propia, en
 * `console/merchants/:code/clientes`.
 *
 * 🔴 Era la pestaña `clientes` de la ficha del comercio, y es la que más
 * ganaba con el reparto: un comercio puede tener cientos de miles de
 * afiliados, así que la pestaña se cargaba perezosa para no castigar a quien
 * entraba a la ficha por cualquier otra cosa. Con ruta propia eso deja de ser
 * un truco: quien pide esta dirección es porque quiere esta lista.
 *
 * 🔴 El cliente es de la organización, NO del comercio. Su cupo y su historia
 * son de CrediTotal; esta lista sólo dice dónde compra y permite ponerle un
 * tope por comercio sin tocar su cupo global.
 *
 * 📍 El paginado es del SERVIDOR, distinto al resto de la consola. Y el
 * buscador todavía mira sólo la página cargada —el servidor lo avisa con
 * `busqueda_parcial`—: eso se dice en la pantalla en vez de disimularlo, que
 * el arreglo es del lado del servidor.
 *
 * Los filtros viven en la dirección (`q`, `origen`, `pagina`): así el enlace se
 * comparte tal cual y «atrás» deshace el último cambio. Antes vivían en
 * memoria y recargar la página los perdía.
 */
@Component({
  selector: 'app-comercios-clientes',
  templateUrl: './comercios-clientes.component.html',
  styleUrls: ['./comercios-clientes.component.css']
})
export class ComerciosClientesComponent implements OnInit, OnDestroy {

  codigo = '';
  comercio: any = null;

  cargando = true;
  noEncontrado = false;

  clientes: any[] = [];
  clientesTotal = 0;
  clientesCargados = false;
  busquedaParcial = false;

  // ── El filtro, tal como está escrito en la dirección ──────────────────────
  busqueda = '';
  fuente = '';
  pagina = 1;
  readonly porPagina = 25;

  readonly FUENTES: any = {
    DERIVED:  'Derivada de sus compras',
    PURCHASE: 'Nació de una compra',
    MANUAL:   'Cargada a mano'
  };

  private subParams?: Subscription;
  private subQuery?: Subscription;

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    public _auth: AuthService
  ) { }

  /** 📍 `paramMap` emite ya al suscribirse: `codigo` queda puesto antes de que
   *  la suscripción de los filtros dispare la primera búsqueda. */
  ngOnInit(): void {
    this.subParams = this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargarComercio();
    });

    // 🔴 La dirección es la única fuente de verdad: el formulario no busca,
    // ESCRIBE la dirección, y es esta suscripción —que también corre al apretar
    // «atrás»— la que pide los datos.
    this.subQuery = this.ruta.queryParams.subscribe((qp: Params) => {
      this.busqueda = qp['q'] || '';
      this.fuente = qp['origen'] || '';
      this.pagina = qp['pagina'] ? Number(qp['pagina']) : 1;
      this.cargarClientes();
    });
  }

  ngOnDestroy(): void {
    this.subParams?.unsubscribe();
    this.subQuery?.unsubscribe();
  }

  puede = (accion: string): boolean => this._auth.hasPermission(`config.comercios.${accion}`);

  /** Sin `trackBy` los botones de la fila dejan de responder cuando Angular la
   *  rehace: el `mousedown` cae en un nodo y el `mouseup` en otro. */
  porId = (_i: number, af: any): string => af?._id;

  /** Sólo para el nombre del comercio de la miga de pan y del encabezado. */
  cargarComercio = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrado = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      this.comercio = rep?.merchant || null;
      this.noEncontrado = !this.comercio;
    } catch {
      this.comercio = null;
      this.noEncontrado = true;
    } finally {
      this.cargando = false;
    }
  };

  cargarClientes = async (): Promise<void> => {
    if (!this.codigo) return;
    try {
      const rep = await this._merchant.getMerchantCustomers(this.codigo, {
        pagina: this.pagina,
        porPagina: this.porPagina,
        search: this.busqueda.trim(),
        source: this.fuente
      });
      this.clientes = rep?.affiliations || [];
      this.clientesTotal = rep?.total || 0;
      this.busquedaParcial = !!rep?.busqueda_parcial;
      this.clientesCargados = true;
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudieron cargar los clientes',
                  text: e?.error?.msg || e?.message });
    }
  };

  // ── dirección ⇄ filtro ────────────────────────────────────────────────────

  private actualizarUrl = (reiniciarPagina = true): void => {
    if (reiniciarPagina) this.pagina = 1;
    const qp: any = {};
    if (this.busqueda.trim()) qp.q = this.busqueda.trim();
    if (this.fuente) qp.origen = this.fuente;
    if (this.pagina > 1) qp.pagina = this.pagina;
    this.router.navigate([], { relativeTo: this.ruta, queryParams: qp });
  };

  buscar = (): void => { this.actualizarUrl(); };

  limpiarBusqueda = (): void => {
    this.busqueda = '';
    this.actualizarUrl();
  };

  get totalPaginas(): number {
    return Math.max(1, Math.ceil(this.clientesTotal / this.porPagina));
  }

  irAPagina = (n: number): void => {
    if (n < 1 || n > this.totalPaginas) return;
    this.pagina = n;
    this.actualizarUrl(false);
  };

  /**
   * El sublímite es POR COMERCIO y no toca el cupo global del cliente.
   * Vacío = sin sublímite (usa el global). 0 = no puede comprar aquí.
   */
  cambiarSublimite = async (af: any): Promise<void> => {
    const actual = af.quota_sublimit === null || af.quota_sublimit === undefined
      ? '' : String(af.quota_sublimit);
    const r = await Swal.fire({
      title: 'Sublímite en este comercio',
      html: 'Es un tope <b>solo para este comercio</b>. No cambia el cupo global del cliente.' +
            '<br><br>Déjalo <b>vacío</b> para quitar el sublímite. Un <b>0</b> significa que no puede comprar aquí.',
      input: 'text', inputValue: actual, inputLabel: 'Monto (vacío = sin sublímite)',
      showCancelButton: true, confirmButtonText: 'Guardar el sublímite', cancelButtonText: 'Cancelar',
      inputValidator: (v: string) => {
        if (!v || !v.trim()) return null;
        const n = Number(v);
        return (Number.isNaN(n) || n < 0) ? 'Tiene que ser un número mayor o igual a 0' : null;
      }
    });
    if (!r.isConfirmed) return;
    const valor = (r.value || '').trim() === '' ? null : Number(r.value);
    try {
      const rep = await this._merchant.setCustomerSublimit(af._id, valor);
      await this.cargarClientes();
      Swal.fire({ icon: 'success', title: 'Sublímite actualizado', text: rep?.aviso });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar el sublímite',
                  text: e?.error?.msg || e?.message });
    }
  };
}
